import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod/v4";
import { useId } from "react";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "react-router";
import {
  data,
  Form,
  Link,
  redirect,
  useActionData,
  useSearchParams,
} from "react-router";
import { safeRedirect } from "remix-utils/safe-redirect";
import { z } from "zod";

import { Button } from "#guidelines/Form/Button";
import { Fieldset } from "#guidelines/Form/Fieldset";
import FormErrors from "#guidelines/Form/FormErrors";
import { Input } from "#guidelines/Form/Input";
import { AuthenticationLayout } from "#guidelines/Layout/AuthenticationLayout";
import {
  authenticator,
  FormStrategyAuthorizationError,
  type UserIdentity,
  withFormStrategy,
} from "#shared/Security/Authenticator.server";
import {
  findUserIdentityInSession,
  sessionStorage,
  setUserInSession,
} from "#shared/Session.server";

const schema = z.object({
  email: z.string().email(),
  password: z.string(),
  redirectTo: z.string().optional(),
});

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const userIdentity = await findUserIdentityInSession(request);
  if (userIdentity) {
    throw redirect("/pepettes");
  }

  return null;
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.clone().formData();

  const submission = await parseWithZod(formData, {
    async: true,
    schema,
  });

  if (submission.status !== "success") {
    return data(submission.reply(), {
      status: submission.status === "error" ? 400 : 200,
    });
  }

  let userIdentity: UserIdentity;
  try {
    userIdentity = await authenticator.authenticate(withFormStrategy, request);
  } catch (error) {
    if (error instanceof FormStrategyAuthorizationError) {
      return data(submission.reply({ formErrors: [error.message] }), {
        status: 400,
      });
    }

    throw error;
  }

  const session = await setUserInSession(request, userIdentity);

  throw redirect(safeRedirect(submission.value.redirectTo, "/"), {
    headers: {
      "Set-Cookie": await sessionStorage.commitSession(session),
    },
  });
};

export const meta: MetaFunction = () => {
  return [
    {
      title: "Login",
    },
  ];
};

export default function LoginPage() {
  const lastResult = useActionData<typeof action>();
  const [searchParams] = useSearchParams();

  const formId = useId();

  const [form, { email, password, redirectTo }] = useForm({
    id: formId,
    defaultValue: {
      redirectTo: searchParams.get("redirectTo") ?? undefined,
    },
    lastResult,
    constraint: getZodConstraint(schema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
  });

  return (
    <AuthenticationLayout title="Sign in to your account">
      <Form
        method="post"
        {...getFormProps(form)}
        className="space-y-4 md:space-y-6"
      >
        <Fieldset field={email} label="Email address">
          <Input
            {...getInputProps(email, { type: "email" })}
            autoComplete="email"
            autoFocus
          />
        </Fieldset>

        <Fieldset field={password} label="Password">
          <Input {...getInputProps(password, { type: "password" })} />
        </Fieldset>

        <input
          {...getInputProps(redirectTo, {
            type: "hidden",
            hidden: true,
          })}
        />

        <FormErrors errors={form.errors} />

        <Fieldset>
          <Button type="submit" className="btn-primary">
            Sign in
          </Button>
        </Fieldset>

        <p className="text-sm font-light text-gray-500">
          Don’t have an account yet?{" "}
          <Link
            to={{
              pathname: "/join",
              search: searchParams.toString(),
            }}
            className="link link-accent"
          >
            Sign up
          </Link>
        </p>
      </Form>
    </AuthenticationLayout>
  );
}
